// Indicative financing math — shared by the listing simulator and the PRO tab.
// Purely client-side so monthly payments react in real time to the dealer's
// custom settings (TAEG, apport, durée, valeur résiduelle). Indicative only.

export const clampNum = (v, min, max) => Math.min(max, Math.max(min, Number(v) || 0));

// Classic amortised credit.
export function computeCredit({ price, down, taeg, months }) {
  const P = Math.max(0, (Number(price) || 0) - (Number(down) || 0));
  const n = Math.max(1, Number(months) || 1);
  const i = (Number(taeg) || 0) / 1200;
  const monthly = i > 0 ? (P * i) / (1 - Math.pow(1 + i, -n)) : P / n;
  const totalPaid = monthly * n + (Number(down) || 0);
  const interest = monthly * n - P;
  return {
    principal: P,
    monthly: Math.max(0, monthly),
    totalPaid,
    interest: Math.max(0, interest),
    buyback: 0,
  };
}

// Leasing / LOA — finance the depreciation, interest on the average capital,
// residual value = purchase option paid at term.
export function computeLeasing({ price, down, taeg, months, residualPct }) {
  const pr = Number(price) || 0;
  const financed = Math.max(0, pr - (Number(down) || 0));
  const n = Math.max(1, Number(months) || 1);
  const i = (Number(taeg) || 0) / 1200;
  const rv = pr * ((Number(residualPct) || 0) / 100);
  const depreciation = Math.max(0, financed - rv) / n;
  const interest = ((financed + rv) / 2) * i;
  const monthly = depreciation + interest;
  const totalPaid = monthly * n + (Number(down) || 0);
  return {
    principal: financed,
    monthly: Math.max(0, monthly),
    totalPaid,
    interest: Math.max(0, interest * n),
    buyback: rv,
  };
}

export function computeMonthly(mode, args) {
  return mode === "leasing" ? computeLeasing(args) : computeCredit(args);
}

// Remaining-capital (credit) or paid-vs-residual (leasing) series for the chart.
export function buildSeries(mode, { price, down, taeg, months, residualPct }) {
  const n = Math.max(1, Number(months) || 1);
  const step = n > 24 ? Math.ceil(n / 24) : 1;
  const out = [];
  if (mode === "leasing") {
    const { monthly, buyback } = computeLeasing({ price, down, taeg, months, residualPct });
    for (let m = 0; m <= n; m += step) {
      out.push({ m, paid: Math.round((Number(down) || 0) + monthly * m), residual: Math.round(buyback) });
    }
    if (out[out.length - 1].m !== n) out.push({ m: n, paid: Math.round((Number(down) || 0) + monthly * n), residual: Math.round(buyback) });
    return out;
  }
  const P = Math.max(0, (Number(price) || 0) - (Number(down) || 0));
  const i = (Number(taeg) || 0) / 1200;
  const { monthly } = computeCredit({ price, down, taeg, months });
  let bal = P;
  for (let m = 0; m <= n; m += 1) {
    if (m === 0) { if (m % step === 0) out.push({ m, capital: Math.round(bal) }); continue; }
    const int = bal * i;
    bal = Math.max(0, bal - (monthly - int));
    if (m % step === 0 || m === n) out.push({ m, capital: Math.round(bal) });
  }
  return out;
}

export const eur = (v) => `${Math.round(Number(v) || 0).toLocaleString("fr-BE")} €`;
